// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
import { MenuItem, Switch, TextField } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import type { TyStoreLink, TyStoreLinkType } from "@/hooks/useStore/types";
import { getTextFieldSx, readLangs, writeLangs } from "@/utils";
import type { TyLangValues } from "@/utils";
import MultiLangInput from "@/UI/inputs/MultiLangInput";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type StoreLinkPopupDetails = {
    /** Absent when adding. Present, with its position, when editing one already in the list. */
    link?: TyStoreLink;
    index?: number;
    onSave: (link: TyStoreLink, index?: number) => void;
};

type Props = {
    onClose: () => void;
    details?: StoreLinkPopupDetails;
};

// --- Constants ----------------------------------------------------------------------------------

/** Mirrors `STORE_LINK_TYPES` in the backend — the order here is the order the picker lists them. */
const LINK_TYPES: TyStoreLinkType[] = ["phone", "whatsapp", "facebook", "instagram", "tiktok", "website", "maps", "custom"];

/** Mirrors `STORE_LINK_PHONE_TYPES` — these hold a number, everything else holds a URL. */
const PHONE_TYPES: TyStoreLinkType[] = ["phone", "whatsapp"];

const switchSx = {
    width: 34,
    height: 21,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: "2.5px",
        transitionDuration: "200ms",
        "&.Mui-checked": {
            transform: "translateX(13px)",
            color: "var(--color-white)",
            "& + .MuiSwitch-track": { backgroundColor: "var(--color-brand)", opacity: 1, border: 0 },
        },
    },
    "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" },
    "& .MuiSwitch-track": { borderRadius: "var(--radius-full)", backgroundColor: "var(--color-gray-01)", opacity: 1 },
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * One link, added or edited. Nothing here reaches the API — the card behind it owns the whole list and
 * saves it in one request, so reordering and editing cannot leave the menu half-updated.
 */
export default function StoreLinkPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { languages, defaultLang } = useStoreLanguages();
    const textFieldSx = getTextFieldSx(lang === "ar" ? "rtl" : "ltr");

    const isEdit = Boolean(details?.link);

    const [type, setType] = useState<TyStoreLinkType>(details?.link?.type ?? "phone");
    const [value, setValue] = useState(details?.link?.value ?? "");
    const [label, setLabel] = useState<TyLangValues>(readLangs(details?.link?.label, languages));
    const [isVisible, setIsVisible] = useState(details?.link?.isVisible ?? true);

    const isPhone = PHONE_TYPES.includes(type);
    const canSave = value.trim().length > 0;

    const handleSave = () => {
        if (!canSave) return;
        details?.onSave(
            { _id: details?.link?._id, type, value: value.trim(), label: writeLangs(label, languages, defaultLang), isVisible },
            details?.index
        );
        onClose();
    };

    return (
        <div className="store-link-popup">
            <div className="slp-header">
                <p className="slp-title">{isEdit ? t.storeLinks.editTitle : t.storeLinks.createTitle}</p>
                <button type="button" className="slp-close" onClick={onClose} aria-label={t.storeLinks.cancelLabel}>
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            <div className="slp-body">
                <TextField
                    select
                    fullWidth
                    label={t.storeLinks.typeLabel}
                    value={type}
                    onChange={(e) => setType(e.target.value as TyStoreLinkType)}
                    sx={textFieldSx}
                >
                    {LINK_TYPES.map((option) => (
                        <MenuItem key={option} value={option}>
                            {t.storeLinks.types[option]}
                        </MenuItem>
                    ))}
                </TextField>

                <div className="slp-field">
                    {/* Always `ltr`: a phone number and a URL both read left-to-right whatever the
                        dashboard's language is, and an Arabic direction mangles both. */}
                    <TextField
                        fullWidth
                        label={t.storeLinks.valueLabel}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        slotProps={{ htmlInput: { dir: "ltr" } }}
                        sx={textFieldSx}
                    />
                    <p className="slp-hint">{isPhone ? t.storeLinks.valuePhoneHint : t.storeLinks.valueUrlHint}</p>
                </div>

                <div className="slp-field">
                    <MultiLangInput
                        label={t.storeLinks.labelLabel}
                        languages={languages}
                        values={label}
                        onChange={(entry, next) => setLabel((prev) => ({ ...prev, [entry]: next }))}
                    />
                    <p className="slp-hint">{t.storeLinks.labelHint}</p>
                </div>

                <div className="slp-toggle">
                    <div className="slp-toggle-text">
                        <span className="slp-toggle-title">{t.storeLinks.visibleLabel}</span>
                        <span className="slp-hint">{t.storeLinks.visibleHint}</span>
                    </div>
                    <Switch checked={isVisible} onChange={(e) => setIsVisible(e.target.checked)} sx={switchSx} />
                </div>
            </div>

            <div className="slp-footer">
                {!canSave && <p className="slp-error">{t.storeLinks.valueRequired}</p>}
                <button type="button" className="slp-btn slp-btn--cancel" onClick={onClose}>
                    {t.storeLinks.cancelLabel}
                </button>
                <button type="button" className="slp-btn slp-btn--save" onClick={handleSave} disabled={!canSave}>
                    {t.storeLinks.saveLabel}
                </button>
            </div>
        </div>
    );
}
